/**
 * Tabs
 *
 * Accessible tab system with autoplay, keyboard navigation, and optional animations.
 * Index-based matching: first button controls first panel, second controls second, etc.
 *
 * Features:
 * - Index-based tab/panel matching (no manual ID configuration needed)
 * - Optional autoplay with configurable duration
 * - CSS progress variable for visual countdown (--_hs---progress)
 * - Pause on hover and focus (accessibility)
 * - Previous/next/toggle controls
 * - Keyboard navigation (Arrow keys, Home, End)
 * - Optional fade or height animations
 * - Respects prefers-reduced-motion
 * - Full ARIA support (auto-generated IDs, aria-controls, aria-selected)
 *
 * Structure:
 *   <div data-hs-tab="wrapper" data-hs-tab-autoplay="true" data-hs-tab-duration="6">
 *     <div data-hs-tab="links" role="tablist">
 *       <div class="tab-link">
 *         <button role="tab" data-hs-clickable="button">Tab 1</button>
 *       </div>
 *     </div>
 *     <div data-hs-tab="list">
 *       <div role="tabpanel">Panel 1 content</div>
 *       <div role="tabpanel">Panel 2 content</div>
 *     </div>
 *   </div>
 */

import {
  querySelectorAll,
  querySelector,
  getSelector,
  globalConfig,
  prefersReducedMotion,
  getGsap,
  hasGsap,
} from '@utils';

interface EventHandler {
  element: Element | Document;
  event: string;
  handler: EventListener;
  options?: AddEventListenerOptions;
}

interface ModuleConfig {
  attributes: {
    elements: Record<string, { primary: string; aliases?: string[] }>;
    properties?: Record<string, string>;
  };
  cssVars?: {
    progress?: string;
  };
}

export function init(moduleConfig: ModuleConfig) {
  const cleanup = {
    handlers: [] as EventHandler[],
    timers: [] as number[],
    animationFrames: [] as number[],
    observers: [] as IntersectionObserver[],
  };

  const addHandler = (
    element: Element | Document,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void => {
    element.addEventListener(event, handler, options);
    cleanup.handlers.push({ element, event, handler, options });
  };

  const addTimer = (id: number) => cleanup.timers.push(id);
  const addAnimationFrame = (id: number) => cleanup.animationFrames.push(id);
  const addObserver = (obs: IntersectionObserver) => cleanup.observers.push(obs);

  // Helper: Generate unique IDs for ARIA
  const generateId = (prefix: string) => `${prefix}-${Math.random().toString(36).substring(2, 9)}`;

  // Helper: Get CSS var name with prefix
  const getProgressVarName = () =>
    `${globalConfig.cssVars.prefix}${moduleConfig.cssVars?.progress || 'progress'}`;

  // Helper: Set progress CSS variable on element
  const setProgress = (element: HTMLElement, progress: number) =>
    element.style.setProperty(getProgressVarName(), String(progress));

  // Helper: Fade animation using GSAP
  async function animateFade(
    outPanel: HTMLElement,
    inPanel: HTMLElement,
    duration: number
  ): Promise<void> {
    // Handle reduced motion by setting duration to 0
    if (prefersReducedMotion()) duration = 0;

    const gsapInstance = getGsap('tabs', false);

    // Fallback to instant switch if GSAP not available
    if (!gsapInstance) {
      outPanel.style.display = 'none';
      outPanel.style.position = '';
      (outPanel as HTMLElement).inert = true;
      outPanel.style.pointerEvents = 'none';

      inPanel.style.display = 'block';
      inPanel.style.position = '';
      (inPanel as HTMLElement).inert = false;
      inPanel.style.pointerEvents = '';
      return;
    }

    const { gsap } = gsapInstance;

    return new Promise<void>((resolve) => {
      const tl = gsap.timeline({
        onComplete: () => {
          // Final cleanup after animation
          outPanel.style.display = 'none';
          outPanel.style.position = '';
          outPanel.style.opacity = '';
          (outPanel as HTMLElement).inert = true;
          outPanel.style.pointerEvents = 'none';

          inPanel.style.position = '';
          inPanel.style.opacity = '';
          (inPanel as HTMLElement).inert = false;
          inPanel.style.pointerEvents = '';

          resolve();
        },
        defaults: { duration: duration, ease: 'power1.out' },
      });

      // Set old panel to absolute position
      tl.set(outPanel, { position: 'absolute', top: 0, left: 0, width: '100%' });

      // Set new panel to visible and relative
      tl.set(inPanel, { display: 'block', position: 'relative', opacity: 0 });

      // Fade out old panel
      tl.to(outPanel, { opacity: 0 });

      // Fade in new panel (starts at 50% of fade out)
      tl.to(inPanel, { opacity: 1 }, '<50%');
    });
  }

  // Helper: Clip-path animation using GSAP
  async function animateClip(
    outPanel: HTMLElement,
    inPanel: HTMLElement,
    duration: number
  ): Promise<void> {
    // Handle reduced motion by setting duration to 0
    if (prefersReducedMotion()) duration = 0;

    const gsapInstance = getGsap('tabs', false);

    // Fallback to instant switch if GSAP not available
    if (!gsapInstance) {
      outPanel.style.display = 'none';
      outPanel.style.position = '';
      outPanel.style.clipPath = '';
      (outPanel as HTMLElement).inert = true;
      outPanel.style.pointerEvents = 'none';

      inPanel.style.display = 'block';
      inPanel.style.position = '';
      inPanel.style.clipPath = '';
      (inPanel as HTMLElement).inert = false;
      inPanel.style.pointerEvents = '';
      return;
    }

    const { gsap } = gsapInstance;

    return new Promise<void>((resolve) => {
      const tl = gsap.timeline({
        onComplete: () => {
          // Final cleanup after animation
          outPanel.style.display = 'none';
          outPanel.style.position = '';
          outPanel.style.clipPath = '';
          (outPanel as HTMLElement).inert = true;
          outPanel.style.pointerEvents = 'none';

          inPanel.style.position = '';
          inPanel.style.clipPath = '';
          (inPanel as HTMLElement).inert = false;
          inPanel.style.pointerEvents = '';

          resolve();
        },
        defaults: { duration: duration, ease: 'power1.inOut' },
      });

      // Set old panel to absolute position with full clip
      tl.set(outPanel, {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        clipPath: 'inset(0% 0% 0% 0%)',
      });

      // Set new panel to visible with bottom clipped
      tl.set(inPanel, { display: 'block', position: 'relative', clipPath: 'inset(0% 0% 100% 0%)' });

      // Clip out old panel (top to bottom)
      tl.to(outPanel, { clipPath: 'inset(100% 0% 0% 0%)' });

      // Clip in new panel (bottom to top, starts at 40% through clip out)
      tl.to(inPanel, { clipPath: 'inset(0% 0% 0% 0%)' }, '<40%');
    });
  }

  // Setup individual tab system
  function setupTabSystem(wrapper: Element): void {
    const linksWrapper = querySelector(moduleConfig as any, 'links', wrapper);
    const listWrapper = querySelector(moduleConfig as any, 'list', wrapper);

    if (!linksWrapper || !listWrapper) {
      return;
    }

    // Get tab links (direct children of links wrapper)
    const tabLinkElements = Array.from(linksWrapper.children);

    // Get tab panels (direct children of list wrapper)
    const tabPanels = Array.from(listWrapper.children) as HTMLElement[];

    // Find clickable buttons within each tab link element
    const clickableSelector = getSelector(globalConfig.clickable as any, 'button');
    const tabButtons = tabLinkElements
      .map((linkEl) => linkEl.querySelector(clickableSelector))
      .filter((btn) => btn !== null) as HTMLElement[];

    if (tabButtons.length === 0) {
      return;
    }

    // Get options from attributes
    const autoplayAttr = (wrapper as HTMLElement).getAttribute(
      moduleConfig.attributes.properties?.autoplay || 'data-hs-tab-autoplay'
    );
    const durationAttr = (wrapper as HTMLElement).getAttribute(
      moduleConfig.attributes.properties?.duration || 'data-hs-tab-duration'
    );
    const hoverAttr = (wrapper as HTMLElement).getAttribute(
      moduleConfig.attributes.properties?.hover || 'data-hs-tab-hover'
    );
    const animationAttr = (wrapper as HTMLElement).getAttribute(
      moduleConfig.attributes.properties?.animation || 'data-hs-tab-animation'
    );
    const animDurationAttr = (wrapper as HTMLElement).getAttribute(
      moduleConfig.attributes.properties?.animationDuration || 'data-hs-tab-animation-duration'
    );

    const autoplayEnabled = autoplayAttr === 'true';
    const duration = durationAttr ? parseFloat(durationAttr) : 6;
    const pauseOnHover = hoverAttr === 'pause';
    const animationType = animationAttr as 'fade' | 'clip' | null;
    const animationDuration = animDurationAttr ? parseFloat(animDurationAttr) : 0.3;

    // Generate unique ID for this tab system
    const tabSystemId = generateId('hs-tab');

    // Setup ARIA and IDs
    tabPanels.forEach((panel, index) => {
      const panelId = panel.id || `${tabSystemId}-panel-${index}`;
      const tabId = `${tabSystemId}-tab-${index}`;

      panel.id = panelId;
      if (tabButtons[index]) {
        tabButtons[index].id = tabId;
        tabButtons[index].setAttribute('aria-controls', panelId);
      }
    });

    // State
    let currentIndex = 0;
    let autoplayTimer: number | null = null;
    let progressAnimationFrame: number | null = null;
    let progressStartTime: number | null = null;
    let isPaused = false;
    let isTransitioning = false;
    let isInView = false;

    // Activate tab
    async function activateTab(index: number, skipAnimation = false): Promise<void> {
      // Block if transition in progress
      if (isTransitioning) return;

      // Validate index
      if (index < 0 || index >= tabPanels.length) return;
      if (index === currentIndex && !skipAnimation) return;

      // Lock IMMEDIATELY to block any further calls
      isTransitioning = true;

      const previousIndex = currentIndex;
      currentIndex = index;

      // Update button ARIA and active classes
      tabButtons.forEach((btn, i) => {
        btn.setAttribute('aria-selected', i === index ? 'true' : 'false');
        btn.setAttribute('tabindex', i === index ? '0' : '-1');
      });

      tabLinkElements.forEach((linkEl, i) => {
        linkEl.classList.toggle(globalConfig.classes.active, i === index);
      });

      // Handle progress transfer
      if (autoplayEnabled) {
        if (!isPaused) {
          startAutoplay();
        } else {
          const currentProgress = tabLinkElements[previousIndex]
            ? parseFloat(
                getComputedStyle(tabLinkElements[previousIndex] as HTMLElement).getPropertyValue(
                  getProgressVarName()
                ) || '0'
              )
            : 0;

          tabLinkElements.forEach((linkEl, i) =>
            setProgress(linkEl as HTMLElement, i === index ? currentProgress : 0)
          );
        }
      }

      // Update panel visibility with animation
      const previousPanel = tabPanels[previousIndex];
      const currentPanel = tabPanels[index];

      if (animationType === 'fade' && !skipAnimation) {
        await animateFade(previousPanel, currentPanel, animationDuration);
      } else if (animationType === 'clip' && !skipAnimation) {
        await animateClip(previousPanel, currentPanel, animationDuration);
      } else {
        // Instant switch (no animation or skip)
        tabPanels.forEach((panel, i) => {
          if (i === index) {
            if (animationType === 'fade' || animationType === 'clip') {
              (panel as HTMLElement).style.position = '';
              (panel as HTMLElement).style.display = 'block';
              if (animationType === 'fade') {
                (panel as HTMLElement).style.opacity = '1';
              } else {
                (panel as HTMLElement).style.clipPath = '';
              }
            } else {
              (panel as HTMLElement).style.display = 'block';
            }
            (panel as HTMLElement).inert = false;
            (panel as HTMLElement).style.pointerEvents = '';
            panel.classList.add(globalConfig.classes.active);
          } else {
            if (animationType === 'fade' || animationType === 'clip') {
              (panel as HTMLElement).style.display = 'none';
              if (animationType === 'fade') {
                (panel as HTMLElement).style.opacity = '0';
              } else {
                (panel as HTMLElement).style.clipPath = '';
              }
            } else {
              (panel as HTMLElement).style.display = 'none';
            }
            (panel as HTMLElement).inert = true;
            (panel as HTMLElement).style.pointerEvents = 'none';
            panel.classList.remove(globalConfig.classes.active);
          }
        });
      }

      // Release lock
      isTransitioning = false;
    }

    // Progress animation
    function updateProgress(timestamp: number): void {
      if (autoplayEnabled) {
        if (!progressStartTime) progressStartTime = timestamp;
        const progress = Math.min((timestamp - progressStartTime) / (duration * 1000), 1);

        tabLinkElements.forEach((linkEl, i) =>
          setProgress(linkEl as HTMLElement, i === currentIndex ? progress : 0)
        );

        if (progress < 1 && !isPaused) {
          progressAnimationFrame = requestAnimationFrame(updateProgress);
          if (progressAnimationFrame) addAnimationFrame(progressAnimationFrame);
        }
      } else {
        tabLinkElements.forEach((linkEl, i) =>
          setProgress(linkEl as HTMLElement, i === currentIndex ? 1 : 0)
        );
      }
    }

    // Start autoplay
    function startAutoplay(): void {
      stopAutoplay();
      if (!autoplayEnabled) return updateProgress(0);
      if (isPaused || !isInView) return;

      // Get current progress to resume from (if any)
      const currentProgress = tabLinkElements[currentIndex]
        ? parseFloat(
            getComputedStyle(tabLinkElements[currentIndex] as HTMLElement).getPropertyValue(
              getProgressVarName()
            ) || '0'
          )
        : 0;

      // Calculate remaining time based on current progress
      const elapsedTime = currentProgress * duration * 1000; // Convert progress to milliseconds
      const remainingTime = Math.max(0, duration * 1000 - elapsedTime);

      // Start progress animation from current progress
      progressStartTime = null;
      const startProgress = currentProgress;
      const resumeStartTime = performance.now();

      const resumeUpdate = (timestamp: number): void => {
        const totalProgress = Math.min(
          startProgress + (timestamp - resumeStartTime) / (duration * 1000),
          1
        );

        tabLinkElements.forEach((linkEl, i) =>
          setProgress(linkEl as HTMLElement, i === currentIndex ? totalProgress : 0)
        );

        if (totalProgress < 1 && !isPaused) {
          progressAnimationFrame = requestAnimationFrame(resumeUpdate);
          if (progressAnimationFrame) addAnimationFrame(progressAnimationFrame);
        }
      };

      progressAnimationFrame = requestAnimationFrame(resumeUpdate);
      if (progressAnimationFrame) addAnimationFrame(progressAnimationFrame);

      autoplayTimer = window.setTimeout(
        () => activateTab((currentIndex + 1) % tabPanels.length),
        remainingTime
      );
      addTimer(autoplayTimer);
    }

    // Stop autoplay
    function stopAutoplay(): void {
      if (autoplayTimer !== null) {
        clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }
      if (progressAnimationFrame !== null) {
        cancelAnimationFrame(progressAnimationFrame);
        progressAnimationFrame = null;
      }
      progressStartTime = null;
    }

    // Pause/Resume autoplay
    const pauseAutoplay = () => {
      isPaused = true;
      stopAutoplay();
    };
    const resumeAutoplay = () => {
      isPaused = false;
      startAutoplay();
    };

    // Tab click handler
    tabButtons.forEach((button, index) => {
      addHandler(button, 'click', (e: Event) => {
        e.preventDefault();
        activateTab(index);
      });
    });

    // Keyboard navigation
    const keydownHandler = (e: KeyboardEvent): void => {
      const index = tabButtons.indexOf(e.target as HTMLElement);
      if (index === -1) return;

      let newIndex = index;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          newIndex = index === 0 ? tabButtons.length - 1 : index - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          newIndex = index === tabButtons.length - 1 ? 0 : index + 1;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = tabButtons.length - 1;
          break;
        default:
          return;
      }

      if (isTransitioning) return;
      activateTab(newIndex);
      tabButtons[newIndex].focus();
    };

    tabButtons.forEach((button) => addHandler(button, 'keydown', keydownHandler));

    // Pause on hover
    if (pauseOnHover && autoplayEnabled) {
      addHandler(wrapper, 'mouseenter', pauseAutoplay);
      addHandler(wrapper, 'mouseleave', resumeAutoplay);
    }

    // Pause on focus (keyboard only)
    let isUsingKeyboard = false;
    addHandler(document, 'keydown', () => (isUsingKeyboard = true));
    addHandler(document, 'mousedown', () => (isUsingKeyboard = false));

    if (autoplayEnabled) {
      addHandler(wrapper, 'focusin', () => {
        if (isUsingKeyboard) {
          pauseAutoplay();
          updateToggleState();
        }
      });
      addHandler(wrapper, 'focusout', () => {
        if (isUsingKeyboard && isPaused) {
          resumeAutoplay();
          updateToggleState();
        }
      });
    }

    // Previous/Next/Toggle controls
    const controlsSelector = `[${
      moduleConfig.attributes.properties?.controls || 'data-hs-tab-controls'
    }]`;
    const controls = wrapper.querySelectorAll(controlsSelector);

    // Find toggle button for state updates (may be null if no toggle or autoplay disabled)
    let toggleControl: Element | null = null;
    let toggleButton: HTMLElement | null = null;

    controls.forEach((control) => {
      const controlType = control.getAttribute(
        moduleConfig.attributes.properties?.controls || 'data-hs-tab-controls'
      );
      const controlButton = control.querySelector(clickableSelector);

      if (!controlButton) return;

      if (controlType === 'previous') {
        (controlButton as HTMLElement).setAttribute('aria-label', 'Previous tab');
        addHandler(controlButton, 'click', (e: Event) => {
          e.preventDefault();
          activateTab(currentIndex === 0 ? tabPanels.length - 1 : currentIndex - 1);
        });
      } else if (controlType === 'next') {
        (controlButton as HTMLElement).setAttribute('aria-label', 'Next tab');
        addHandler(controlButton, 'click', (e: Event) => {
          e.preventDefault();
          activateTab((currentIndex + 1) % tabPanels.length);
        });
      } else if (controlType === 'toggle') {
        // Hide toggle button if autoplay is disabled
        if (!autoplayEnabled) {
          (control as HTMLElement).style.display = 'none';
          return;
        }

        // Store references for updateToggleState function
        toggleControl = control;
        toggleButton = controlButton as HTMLElement;

        toggleButton.setAttribute('aria-label', isPaused ? 'Play' : 'Pause');
        toggleControl.classList.toggle(globalConfig.classes.active, !isPaused);

        addHandler(controlButton, 'click', (e: Event) => {
          e.preventDefault();
          isPaused ? resumeAutoplay() : pauseAutoplay();
          updateToggleState();
        });
      }
    });

    // Helper: Update toggle button state
    const updateToggleState = () => {
      if (!toggleControl || !toggleButton) return;
      toggleControl.classList.toggle(globalConfig.classes.active, !isPaused);
      toggleButton.setAttribute('aria-label', isPaused ? 'Play' : 'Pause');
    };

    // Initialize first tab as active
    activateTab(0, true);

    // IntersectionObserver for autoplay visibility control
    if (autoplayEnabled) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            isInView = entry.isIntersecting;
            entry.isIntersecting && !isPaused ? startAutoplay() : stopAutoplay();
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(wrapper);
      addObserver(observer);
    }
  }

  // Find all tab wrappers and initialize
  const tabWrappers = querySelectorAll(moduleConfig as any, 'wrapper');

  tabWrappers.forEach((wrapper) => {
    setupTabSystem(wrapper);
  });

  return {
    result: `tabs initialized (${tabWrappers.length} systems)`,
    destroy: () => {
      // Clear all timers
      cleanup.timers.forEach((timerId) => clearTimeout(timerId));
      cleanup.timers.length = 0;

      // Clear all animation frames
      cleanup.animationFrames.forEach((frameId) => cancelAnimationFrame(frameId));
      cleanup.animationFrames.length = 0;

      // Disconnect all observers
      cleanup.observers.forEach((observer) => observer.disconnect());
      cleanup.observers.length = 0;

      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
      cleanup.handlers.length = 0;
    },
  };
}
